/* eslint-disable no-param-reassign */
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { LayoutChangeEvent, Platform, View, ViewStyle } from 'react-native';

import Animated, {
    AnimatedStyleProp,
    Easing,
    ReduceMotion,
    runOnUI,
    scrollTo,
    useAnimatedRef,
    useDerivedValue,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { useRoute } from '@react-navigation/core';

import { useCellSelectionContext } from './CellSelectionProvider';
import ChannelList from './ChannelList';
import { DEFAULT_CONFIG } from './Config/default';
import DataRequester from './DataRequester';
import NowIndicatorLine from './NowIndicatorLine';
import {
    DataProvider,
    IRecyclerGridView,
    LayoutProvider,
    RecyclerGridView,
} from './RecyclerGridView';
import { CellItem, CellType, Layout } from './RecyclerGridView/types';
import FloatingCell from './Renderers/GridCellRenderer/FloatingCell';
import NormalCell from './Renderers/GridCellRenderer/NormalCell';
import TopLeftAccessoryRenderer from './Renderers/TopLeftAccessoryRenderer';
import TimeBar from './TimeBar';
import useEpgDragHandlers from './hooks/useEpgDragHandlers';
import { EPGAlignment, EPGChannel, EPGConfig, EPGProps, IEPG } from './types';
import {
    channelIdFromLoadingCellId,
    convertOffsetToTime,
    convertTimeToOffset,
    createNoDataCellIdFromChannelId,
    createProgramsGrid,
    hoursInMilliseconds,
} from './utils';

const useTVEventHandler = Platform.isTV
    ? require('react-native').useTVEventHandler
    : () => {};
const TVEventControl = Platform.isTV
    ? require('react-native').TVEventControl
    : {
          enableTVPanGesture: () => {},
          disableTVPanGesture: () => {},
      };

const TV_EVENT_DELAY_INTERVAL = 100;
const TV_PAN_EVENT_MIN_VELOCITY = 500;

const EPGComponent = forwardRef<IEPG, EPGProps<any, any>>(
    (
        {
            style,
            config,
            channels,
            programs,
            startTime,
            horizontalScrollEnabled = !Platform.isTV,
            verticalScrollEnabled,
            isActive = true,
            onLayout,
            onVerticalScroll,
            onHorizontalScroll,
            onProgramSelected,
            onProgramPressTV,
            onChannelPress,
            onChannelAccessibilityAction,
            onNowMarkerPress,
            needProgramsForChannels,
            renderTopLeftAccessoryView,
            renderChannelCell,
            renderHourCell,
            renderProgramCell,
            renderMissingDataCell,
            renderLoadingRow,
            renderNoDataRow,
            renderNowLine,
            renderNowMarker,
            isGridFocusedRef,
            screenReaderEnabled,
            appState,
        },
        ref,
    ) => {
        const finalConfig: EPGConfig = useMemo(() => {
            const mergedConfig: EPGConfig = {
                ...DEFAULT_CONFIG,
                ...config,
            };

            DataRequester.needDataDelay = mergedConfig.needDataDelay;
            DataRequester.idlePeriod = mergedConfig.idlePeriod;
            DataRequester.preloadEnabled = mergedConfig.preloadEnabled;
            DataRequester.idleRequestCount = mergedConfig.idleRequestCount;

            return {
                ...mergedConfig,
                renderAheadOffsetHorizontal:
                    config.renderAheadOffsetHorizontal ??
                    (Platform.isTV
                        ? mergedConfig.tvHorizontalScrollAmount
                        : mergedConfig.renderAheadOffsetHorizontal),
            };
        }, []);
        const { setSelectedCell, selectedCellRef } = useCellSelectionContext();
        const route = useRoute();

        const tvPanEventHandled = useRef(false);
        const tvEventDelayTimer = useRef<NodeJS.Timeout>(undefined);
        const gridRef = useRef<IRecyclerGridView | null>(null);
        const channelListRef =
            useAnimatedRef<Animated.FlatList<EPGChannel<any>>>();
        const verticalScrollViewRef = useAnimatedRef<Animated.ScrollView>();
        const horizontalScrollViewRef = useAnimatedRef<Animated.ScrollView>();
        const timeBarRef = useAnimatedRef<Animated.ScrollView>();

        const animatingYOffset = useSharedValue<boolean>(false);
        const animatingXOffset = useSharedValue<boolean>(false);
        const xTargetOffset = useSharedValue<number>(0);
        const yVisibleStartOffset = useSharedValue<number>(0);
        const xVisibleStartOffset = useSharedValue<number>(0);
        const yVisibleEndOffset = useSharedValue<number>(0);
        const xVisibleEndOffset = useSharedValue<number>(0);

        const ticker = useRef<NodeJS.Timeout>(undefined);
        const xNowOffset = useSharedValue(0);
        const [showNowMarker, setShowNowMarker] = useState(false);

        const [gridContainerSize, setGridContainerSize] = useState<{
            width: number;
            height: number;
        }>({ width: 0, height: 0 });

        const dataClass = 'epgGrid';

        useDerivedValue(() => {
            xVisibleEndOffset.value =
                xVisibleStartOffset.value + gridContainerSize.width;
            yVisibleEndOffset.value =
                yVisibleStartOffset.value + gridContainerSize.height;
        }, [gridContainerSize]);

        useDerivedValue(() => {
            scrollTo(channelListRef, 0, yVisibleStartOffset.value, false);
            scrollTo(
                verticalScrollViewRef,
                0,
                yVisibleStartOffset.value,
                false,
            );
            scrollTo(timeBarRef, xVisibleStartOffset.value, 0, false);
            scrollTo(
                horizontalScrollViewRef,
                xVisibleStartOffset.value,
                0,
                false,
            );
        }, []);

        useEpgDragHandlers({
            dataClass,
            offsetX: xVisibleStartOffset,
            offsetY: yVisibleStartOffset,
            contentWidth: finalConfig.hourWidth * finalConfig.hoursCount,
            isVertical: true,
            isHorizontal: true,
        });

        const [dataProvider, setDataProvider] = useState(new DataProvider([]));
        const [layoutProvider, setLayoutProvider] = useState(
            new LayoutProvider([]),
        );

        const contentHeight = useMemo(
            () =>
                (Object.keys(channels).length +
                    (Platform.OS === 'web' ? 0.01 : 0)) *
                (finalConfig.rowHeight + finalConfig.padding),
            [channels],
        );

        const verticalScrollStyle = useMemo(
            () =>
                ({
                    marginTop: finalConfig.timebarHeight,
                    marginLeft: finalConfig.channelWidth,
                } as AnimatedStyleProp<ViewStyle>),
            [],
        );

        const _onLayout = useCallback((e: LayoutChangeEvent) => {
            setGridContainerSize(e.nativeEvent.layout);
            onLayout?.(e);
        }, []);

        const _onVerticalScroll = useCallback(
            (offsetX: number) => {
                onVerticalScroll?.(offsetX);
                DataRequester.resetIdleTimer();
            },
            [onVerticalScroll],
        );

        const _onHorizontalScroll = useCallback(
            (offsetX: number) => {
                onHorizontalScroll?.(offsetX);
                DataRequester.resetIdleTimer();
            },
            [onHorizontalScroll],
        );

        const onSelect = useCallback(
            (item: CellItem) => {
                if (item.program) {
                    onProgramSelected?.(item.program);
                }
                setSelectedCell(item);
                DataRequester.resetIdleTimer();
            },
            [onProgramSelected, setSelectedCell],
        );

        const _onProgramPressTV = useCallback(
            (item: CellItem) => {
                if (item.program) {
                    onProgramPressTV?.(item.program);
                }
            },
            [onProgramPressTV],
        );

        const onTick = useCallback(() => {
            const now = Date.now();

            xNowOffset.value = convertTimeToOffset(
                startTime,
                now,
                finalConfig.hourWidth,
            );

            setShowNowMarker(
                now >= startTime &&
                    now - startTime <
                        hoursInMilliseconds(finalConfig.hoursCount),
            );
        }, [startTime]);

        const scrollToXOffset = useCallback(
            (xOffset: number, animated: boolean = true) => {
                const x = Math.min(
                    Math.max(0, xOffset),
                    finalConfig.hourWidth * finalConfig.hoursCount -
                        gridContainerSize.width,
                );

                animatingXOffset.value = true;
                xVisibleStartOffset.value = animated
                    ? withTiming(
                          x,
                          {
                              duration: 400,
                              easing: Easing.out(Easing.quad),
                              reduceMotion: ReduceMotion.System,
                          },
                          () => {
                              animatingXOffset.value = false;
                          },
                      )
                    : x;
                xTargetOffset.value = x;

                if (!animated) {
                    animatingXOffset.value = false;
                }
            },
            [gridContainerSize.width],
        );

        const scrollByTime = useCallback(
            (time: number, animated?: boolean) => {
                const offset =
                    (time / hoursInMilliseconds()) * finalConfig.hourWidth;
                scrollToXOffset(xVisibleStartOffset.value + offset, animated);
            },
            [scrollToXOffset],
        );

        const scrollToTime = useCallback(
            (
                time: number,
                alignment = EPGAlignment.center,
                animated: boolean = true,
            ) => {
                // If time inside current timeline
                if (
                    time >= startTime &&
                    time - startTime <
                        hoursInMilliseconds(finalConfig.hoursCount)
                ) {
                    let x = convertTimeToOffset(
                        startTime,
                        time,
                        finalConfig.hourWidth,
                    ); // left
                    if (alignment === EPGAlignment.center) {
                        x -= gridContainerSize.width / 2;
                    } else if (alignment === EPGAlignment.end) {
                        x -= gridContainerSize.width;
                    }

                    scrollToXOffset(x, animated);
                }
            },
            [startTime, gridContainerSize.width, scrollToXOffset],
        );

        const scrollToNow = useCallback(
            (alignment = EPGAlignment.center, animated?: boolean) => {
                scrollToTime(Date.now(), alignment, animated);
            },
            [scrollToTime],
        );

        const scrollToYOffset = useCallback(
            (yOffset: number, animated: boolean = true) => {
                const y = Math.min(
                    Math.max(0, yOffset),
                    (finalConfig.rowHeight + finalConfig.padding) *
                        channels.length -
                        gridContainerSize.height,
                );

                animatingYOffset.value = true;
                yVisibleStartOffset.value = animated
                    ? withTiming(
                          y,
                          {
                              duration: 400,
                              easing: Easing.out(Easing.quad),
                              reduceMotion: ReduceMotion.System,
                          },
                          () => {
                              animatingYOffset.value = false;
                          },
                      )
                    : y;

                if (!animated) {
                    animatingYOffset.value = false;
                }
            },
            [gridContainerSize.height],
        );

        const scrollToChannel = useCallback(
            (
                channelId: string,
                alignment = EPGAlignment.center,
                animated?: boolean,
            ) => {
                const index = channels.findIndex((ch) => ch.id === channelId);
                if (index !== -1) {
                    const rowHeight =
                        finalConfig.rowHeight + finalConfig.padding;
                    let y = index * rowHeight;
                    if (alignment === EPGAlignment.center) {
                        y -= gridContainerSize.height / 2;
                        y += rowHeight / 2;
                    } else if (alignment === EPGAlignment.end) {
                        y -= gridContainerSize.height;
                        y += rowHeight;
                    }

                    scrollToYOffset(y, animated);
                }
            },
            [channels, gridContainerSize.height, scrollToYOffset],
        );

        const stopScrolling = useCallback(() => {
            const worklet = () => {
                'worklet';

                scrollTo(
                    channelListRef,
                    0,
                    yVisibleStartOffset.value + 0.1,
                    false,
                );
                scrollTo(
                    verticalScrollViewRef,
                    0,
                    yVisibleStartOffset.value + 0.1,
                    false,
                );
                scrollTo(timeBarRef, xVisibleStartOffset.value + 0.1, 0, false);
                scrollTo(
                    horizontalScrollViewRef,
                    xVisibleStartOffset.value + 0.1,
                    0,
                    false,
                );
            };

            runOnUI(worklet)();
        }, []);

        const selectProgram = useCallback(
            (channelId: string, programId: string) => {
                const channelIndex = channels.findIndex(
                    (ch) => ch.id === channelId,
                );
                const cellItem = dataProvider
                    .getAllData()
                    [channelIndex]?.find((ci) => ci.program?.id === programId);

                if (cellItem) {
                    setSelectedCell(cellItem);
                }
            },
            [dataProvider],
        );

        const tvEventLeft = useCallback(() => {
            let needToScroll =
                !!selectedCellRef?.current && isGridFocusedRef?.current;

            if (selectedCellRef.current?.type === CellType.Program) {
                const visibleTimelineStart = convertOffsetToTime(
                    startTime,
                    xVisibleStartOffset.value,
                    finalConfig.hourWidth,
                );
                const programStart = Math.max(
                    startTime,
                    selectedCellRef.current!.program!.startTime,
                );

                if (programStart >= visibleTimelineStart) {
                    needToScroll = false;
                }
            }

            if (needToScroll) {
                scrollByTime(-0.5 * 60 * 60 * 1000);
            }
        }, [startTime, scrollByTime]);

        const tvEventRight = useCallback(() => {
            let needToScroll =
                !!selectedCellRef.current && isGridFocusedRef?.current;

            if (selectedCellRef.current?.type === CellType.Program) {
                const visibleTimelineEnd = convertOffsetToTime(
                    startTime,
                    xVisibleEndOffset.value,
                    finalConfig.hourWidth,
                );
                const programEnd = Math.min(
                    startTime + finalConfig.hoursCount * 1000 * 60 * 60,
                    selectedCellRef.current!.program!.endTime,
                );

                if (programEnd <= visibleTimelineEnd) {
                    needToScroll = false;
                }
            }

            if (needToScroll) {
                scrollByTime(0.5 * 60 * 60 * 1000);
            }
        }, [startTime, scrollByTime]);

        useTVEventHandler((event: any) => {
            switch (event.eventType) {
                case 'left': {
                    clearTimeout(tvEventDelayTimer.current);
                    tvEventDelayTimer.current = setTimeout(() => {
                        tvEventLeft();
                    }, TV_EVENT_DELAY_INTERVAL);
                    break;
                }

                case 'right': {
                    clearTimeout(tvEventDelayTimer.current);
                    tvEventDelayTimer.current = setTimeout(() => {
                        tvEventRight();
                    }, TV_EVENT_DELAY_INTERVAL);
                    break;
                }

                case 'pan': {
                    // @ts-ignore
                    const { state, velocityX, velocityY } = event.body ?? {
                        velocityX: 0,
                        velocityY: 0,
                    };
                    if (state === 'Began') {
                        tvPanEventHandled.current = false;
                    }
                    if (
                        !tvPanEventHandled.current &&
                        Math.abs(velocityX) > Math.abs(velocityY) &&
                        Math.abs(velocityX) > TV_PAN_EVENT_MIN_VELOCITY
                    ) {
                        clearTimeout(tvEventDelayTimer.current);

                        if (velocityX < 0) {
                            tvEventDelayTimer.current = setTimeout(() => {
                                tvEventLeft();
                            }, TV_EVENT_DELAY_INTERVAL);
                        } else {
                            tvEventDelayTimer.current = setTimeout(() => {
                                tvEventRight();
                            }, TV_EVENT_DELAY_INTERVAL);
                        }
                        tvPanEventHandled.current = true;
                    }
                    break;
                }

                default:
                    break;
            }
        });

        const rowRenderer = useCallback(
            (item: CellItem, layout: Layout) => {
                switch (layout.type) {
                    case CellType.Loading:
                        return (
                            <FloatingCell
                                config={finalConfig}
                                item={item}
                                rect={layout}
                                xVisibleStartOffset={xVisibleStartOffset}
                                yVisibleStartOffset={yVisibleStartOffset}
                                xVisibleEndOffset={xVisibleEndOffset}
                                yVisibleEndOffset={yVisibleEndOffset}
                                renderContent={renderLoadingRow}
                                onSelect={onSelect}
                            />
                        );

                    case CellType.NoData:
                        return (
                            <FloatingCell
                                config={finalConfig}
                                item={item}
                                rect={layout}
                                xVisibleStartOffset={xVisibleStartOffset}
                                yVisibleStartOffset={yVisibleStartOffset}
                                xVisibleEndOffset={xVisibleEndOffset}
                                yVisibleEndOffset={yVisibleEndOffset}
                                renderContent={renderNoDataRow}
                                onSelect={onSelect}
                            />
                        );

                    case CellType.MissingData:
                        return (
                            <NormalCell
                                config={finalConfig}
                                item={item}
                                rect={layout}
                                xVisibleStartOffset={xVisibleStartOffset}
                                yVisibleStartOffset={yVisibleStartOffset}
                                xVisibleEndOffset={xVisibleEndOffset}
                                yVisibleEndOffset={yVisibleEndOffset}
                                renderContent={renderMissingDataCell}
                                onSelect={onSelect}
                                screenReaderEnabled={screenReaderEnabled}
                            />
                        );

                    case CellType.Program:
                        return (
                            <NormalCell
                                config={finalConfig}
                                item={item}
                                rect={layout}
                                xVisibleStartOffset={xVisibleStartOffset}
                                yVisibleStartOffset={yVisibleStartOffset}
                                xVisibleEndOffset={xVisibleEndOffset}
                                yVisibleEndOffset={yVisibleEndOffset}
                                onSelect={onSelect}
                                onPressTV={_onProgramPressTV}
                                renderContent={(rowIndex, selected, live) =>
                                    renderProgramCell(
                                        item.program!,
                                        rowIndex,
                                        selected,
                                        live,
                                    )
                                }
                                screenReaderEnabled={screenReaderEnabled}
                                appState={appState}
                            />
                        );

                    default:
                        return null;
                }
            },
            [
                onSelect,
                _onProgramPressTV,
                renderLoadingRow,
                renderNoDataRow,
                renderProgramCell,
                screenReaderEnabled,
                appState,
            ],
        );

        useEffect(() => {
            // Shared value doesn't want to update instantly, only on next tick,
            // so we need like this to put it back for today
            onTick();
            setTimeout(onTick);
            ticker.current = setInterval(
                () => onTick(),
                hoursInMilliseconds() / finalConfig.hourWidth,
            );

            return () => {
                if (ticker.current) {
                    clearInterval(ticker.current);
                    ticker.current = undefined;
                }
            };
        }, [onTick]);

        useEffect(() => {
            DataRequester.isActive = isActive;
            DataRequester.resetIdleTimer();

            if (isActive) {
                TVEventControl.enableTVPanGesture();
                return () => {
                    TVEventControl.disableTVPanGesture();
                };
            }
            return () => {};
        }, [isActive]);

        useLayoutEffect(() => {
            gridRef.current?.reset();
            DataRequester.reset();
            return () => {
                clearTimeout(tvEventDelayTimer.current);
            };
        }, [startTime, channels]);

        useLayoutEffect(() => {
            DataRequester.needProgramsForChannels = needProgramsForChannels;
        }, [needProgramsForChannels]);

        useLayoutEffect(() => {
            const { data, layouts } = createProgramsGrid(
                channels,
                programs,
                startTime,
                finalConfig,
            );

            setDataProvider(new DataProvider(data));
            setLayoutProvider(new LayoutProvider(layouts));

            DataRequester.update(channels, data);

            if (selectedCellRef.current?.type === CellType.Loading) {
                const channelId = channelIdFromLoadingCellId(
                    selectedCellRef.current!.id,
                );
                const programsForLoadingChannel = programs[channelId];
                if (programsForLoadingChannel) {
                    if (programsForLoadingChannel.length === 0) {
                        gridRef.current?.setTVPreferredFocusViewId(
                            createNoDataCellIdFromChannelId(channelId),
                        );
                    } else if (programsForLoadingChannel.length > 0) {
                        const visibleTimelineStart = convertOffsetToTime(
                            startTime,
                            xVisibleStartOffset.value,
                            finalConfig.hourWidth,
                        );
                        const visibleTimelineEnd = convertOffsetToTime(
                            startTime,
                            xVisibleEndOffset.value,
                            finalConfig.hourWidth,
                        );
                        const program = programsForLoadingChannel.find(
                            (p) =>
                                p.endTime > visibleTimelineStart &&
                                p.startTime < visibleTimelineEnd,
                        );
                        if (program) {
                            gridRef.current?.setTVPreferredFocusViewId(
                                program.id,
                            );
                        }
                    }
                }
            }
        }, [channels, programs]);

        useImperativeHandle(
            ref,
            () => ({
                // On WEB back navigation, the scroll offset animated values are restored
                // but scroll view content is not scrolled there, need to call this method
                restoreScrollPosition: () => {
                    xVisibleStartOffset.value += 0.1;
                    yVisibleStartOffset.value += 0.1;
                },

                scrollByTime,
                scrollToTime,
                scrollToNow,

                scrollToChannel,

                selectProgram,
                scrollToYOffset,
            }),
            [
                scrollByTime,
                scrollToNow,
                scrollToTime,
                selectProgram,
                scrollToChannel,
                scrollToYOffset,
            ],
        );

        const Skeleton = useMemo(() => {
            // TODO: maybe will be better to use an image (repeating)
            return channels.map((_, index) => (
                <View
                    // eslint-disable-next-line react/no-array-index-key
                    key={`sk-${index}`}
                    style={{
                        position: 'absolute',
                        left: 0,
                        top:
                            index *
                            (finalConfig.rowHeight + finalConfig.padding),
                        width: '100%',
                        height: finalConfig.rowHeight,
                        backgroundColor: finalConfig.skeletonCellsColor,
                    }}
                />
            ));
        }, [channels]);

        return (
            <View style={style}>
                <RecyclerGridView
                    ref={gridRef}
                    verticalScrollViewRef={verticalScrollViewRef}
                    horizontalScrollViewRef={horizontalScrollViewRef}
                    onLayout={_onLayout}
                    layoutProvider={layoutProvider}
                    dataProvider={dataProvider}
                    rowHeight={finalConfig.rowHeight + finalConfig.padding}
                    backgroundColor={finalConfig.paddingColor}
                    rowRenderer={rowRenderer}
                    Skeleton={Skeleton}
                    renderAheadOffsetHorizontal={
                        finalConfig.renderAheadOffsetHorizontal
                    }
                    renderAheadOffsetVertical={
                        finalConfig.renderAheadOffsetVertical
                    }
                    onIndexesChanged={DataRequester.onIndexesChanged}
                    contentWidth={
                        finalConfig.hourWidth * finalConfig.hoursCount
                    }
                    contentHeight={contentHeight}
                    verticalScrollStyle={verticalScrollStyle}
                    yScrollOffset={yVisibleStartOffset}
                    skipYScrollOffsetUpdate={animatingYOffset}
                    xScrollOffset={xVisibleStartOffset}
                    skipXScrollOffsetUpdate={animatingXOffset}
                    decelerationRate={finalConfig.decelerationRate}
                    onTouchStart={stopScrolling}
                    onVerticalScroll={_onVerticalScroll}
                    onHorizontalScroll={_onHorizontalScroll}
                    horizontalScrollEnabled={horizontalScrollEnabled}
                    verticalScrollEnabled={verticalScrollEnabled}
                    id={`verticalScrollContainer-${route.key}`}
                    dataSet={{ class: dataClass }}
                />
                {showNowMarker && (
                    <NowIndicatorLine
                        config={finalConfig}
                        xScrollOffset={xVisibleStartOffset}
                        xNowOffset={xNowOffset}
                        renderLine={renderNowLine}
                    />
                )}
                <TimeBar
                    listRef={timeBarRef}
                    config={finalConfig}
                    xScrollOffset={xVisibleStartOffset}
                    skipXScrollOffsetUpdate={animatingXOffset}
                    xNowOffset={xNowOffset}
                    startTime={startTime}
                    renderCell={renderHourCell}
                    renderNowMarker={renderNowMarker}
                    onNowMarkerPress={onNowMarkerPress}
                    onTouchStart={stopScrolling}
                    showNowMarker={showNowMarker}
                />
                <ChannelList
                    listRef={channelListRef}
                    config={finalConfig}
                    channels={channels}
                    yScrollOffset={yVisibleStartOffset}
                    skipYScrollOffsetUpdate={animatingYOffset}
                    renderCell={renderChannelCell}
                    onTouchStart={stopScrolling}
                    onCellPress={onChannelPress}
                    onChannelAccessibilityAction={onChannelAccessibilityAction}
                />
                <TopLeftAccessoryRenderer
                    config={finalConfig}
                    renderContent={renderTopLeftAccessoryView}
                />
            </View>
        );
    },
);

export default EPGComponent;
